import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton,
  Box, Typography, CircularProgress,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { RendimientoService } from '../services/rendimientoService';
import type { GrupoTrabajo, GrupoTrabajadorResumen } from '../types/rendimiento';
import type { Trabajador } from '../../trabajadores/types/trabajador.types';

interface Props {
  open: boolean;
  onClose: () => void;
  grupos: GrupoTrabajo[];
  trabajadores: Trabajador[];
  initialGrupoId?: number | null;
}

const GrupoTrabajadorDialog: React.FC<Props> = ({
  open, onClose, grupos, trabajadores, initialGrupoId,
}) => {
  const [selectedGrupo, setSelectedGrupo] = useState<number>(0);
  const [miembros, setMiembros] = useState<GrupoTrabajadorResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [workerToAdd, setWorkerToAdd] = useState<Trabajador | null>(null);

  useEffect(() => {
    if (open && initialGrupoId) {
      setSelectedGrupo(initialGrupoId);
    }
  }, [open, initialGrupoId]);

  useEffect(() => {
    if (selectedGrupo > 0) {
      loadMiembros(selectedGrupo);
    } else {
      setMiembros([]);
    }
  }, [selectedGrupo]);

  const loadMiembros = async (idGrupo: number) => {
    setLoading(true);
    try {
      const data = await RendimientoService.getTrabajadoresGrupo(idGrupo);
      setMiembros(data);
    } catch { setMiembros([]); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!workerToAdd || !selectedGrupo) return;
    try {
      await RendimientoService.asignarTrabajadoresGrupo(selectedGrupo, [workerToAdd.idTrabajador]);
      setWorkerToAdd(null);
      await loadMiembros(selectedGrupo);
    } catch { /* interceptor */ }
  };

  const handleRemove = async (id: number) => {
    try {
      await RendimientoService.removerTrabajadorGrupo(id);
      setMiembros(prev => prev.filter(m => m.idGrupoTrabajador !== id));
    } catch { /* interceptor */ }
  };

  const getWorkerName = (t: Trabajador) =>
    (t as any).personal?.personaContacto || `Trabajador ${t.idTrabajador}`;

  // Filter out already assigned workers
  const assignedIds = new Set(miembros.map(m => m.idTrabajador));
  const availableWorkers = trabajadores.filter(t => !assignedIds.has(t.idTrabajador));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gestionar Trabajadores del Grupo</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} alignItems="center" mt={1} mb={2}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Grupo de Trabajo</InputLabel>
            <Select label="Grupo de Trabajo" value={selectedGrupo}
              onChange={e => setSelectedGrupo(e.target.value as number)}>
              <MenuItem value={0}>-- Seleccionar --</MenuItem>
              {grupos.filter(g => g.activo).map(g => (
                <MenuItem key={g.idGrupoTrabajo} value={g.idGrupoTrabajo}>
                  {g.codigo} - {g.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedGrupo > 0 && (
          <>
            <Box display="flex" gap={1} alignItems="center" mb={2}>
              <Autocomplete
                size="small"
                sx={{ flex: 1 }}
                options={availableWorkers}
                getOptionLabel={getWorkerName}
                value={workerToAdd}
                onChange={(_, v) => setWorkerToAdd(v)}
                renderInput={params => <TextField {...params} label="Agregar trabajador al grupo" />}
              />
              <Button variant="contained" size="small" startIcon={<Add />}
                onClick={handleAdd} disabled={!workerToAdd}>
                Agregar
              </Button>
            </Box>

            {loading ? (
              <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>
            ) : miembros.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No hay trabajadores asignados a este grupo
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Identificaci&oacute;n</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {miembros.map((m, i) => (
                    <TableRow key={m.idGrupoTrabajador}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{m.identificacion}</TableCell>
                      <TableCell>{m.nombreTrabajador}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => handleRemove(m.idGrupoTrabajador)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              Total: {miembros.length} trabajadores
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrupoTrabajadorDialog;
